import { useMemo, useState } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { Radio, ArrowLeft, Square, Copy, Trash2, MoreHorizontal, SlidersHorizontal, Layers } from 'lucide-react';
import { Breadcrumbs, BreadcrumbItem, PageHeader } from '@vnetwork/ui/layout';
import { DataTable, EmptyState, StatusPill, type ColumnDef } from '@vnetwork/ui/data';
import { Card, CardHeader, CardTitle, CardBody, Badge, Button, IconButton, Skeleton, DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem } from '@vnetwork/ui/primitives';
import { CompactStat, CompactStatGroup } from '../../../components/CompactStat';
import { HlsPreviewPlayer } from '../../../_shared/HlsPreviewPlayer';
import { Alert, ConfirmDialog, useToast } from '@vnetwork/ui/feedback';
import { useDrmStream, useStopStream, useDeleteStream, type DrmVariant } from '@/api/aes';
import { trackFeatureAction } from '@/lib/analytics-events';
import {
  streamStatusTone,
  STREAM_STATUS_LABEL,
  kindTone,
  KIND_LABEL,
  fmtBitrate,
  fmtDuration,
} from '../shared';
import { RotationLiveView } from '../rotation/RotationLiveView';
import { StreamTopology } from './StreamTopology';
import { StreamSummaryCard } from './StreamSummaryCard';
import { StreamConfigDialog } from './StreamConfigDialog';
import styles from '../Drm.module.css';

export function StreamDetail({ base }: { base: string }) {
  const navigate = useNavigate();
  // streamId arrives URL-encoded (contains the "app/name" address) → useParams decodes it.
  const { streamId } = useParams<{ streamId: string }>();
  const id = streamId ?? '';
  const { data: stream, isLoading, isError, error } = useDrmStream(id);
  const { show } = useToast();
  const stop = useStopStream();
  const del = useDeleteStream();
  const [pendingStop, setPendingStop] = useState(false);
  const [pendingDelete, setPendingDelete] = useState(false);
  const [pendingConfig, setPendingConfig] = useState(false);

  const copyUrl = () => {
    if (!stream?.output_url) return;
    void navigator.clipboard?.writeText(stream.output_url);
    show({ tone: 'success', title: 'Stream URL copied' });
  };

  const renditionColumns = useMemo<ColumnDef<DrmVariant, unknown>[]>(
    () => [
      { id: 'resolution', header: 'Resolution', cell: ({ row }) => <span style={{ fontFamily: 'var(--vn-font-mono)' }}>{row.original.resolution ?? '—'}</span> },
      { id: 'bitrate', header: 'Bitrate', cell: ({ row }) => <span style={{ color: 'var(--vn-fg-muted)' }}>{fmtBitrate(row.original.bandwidth)}</span> },
      { id: 'codecs', header: 'Codecs', cell: ({ row }) => <span style={{ fontFamily: 'var(--vn-font-mono)', fontSize: 'var(--vn-font-size-sm)', color: 'var(--vn-fg-muted)' }}>{row.original.codecs ?? '—'}</span> },
      { id: 'fps', header: 'FPS', cell: ({ row }) => <span style={{ color: 'var(--vn-fg-muted)' }}>{row.original.frame_rate ?? '—'}</span> },
    ],
    [],
  );

  const peakBitrate = useMemo(() => {
    const ladder = stream?.abr_ladder ?? [];
    return ladder.length ? Math.max(...ladder.map((v) => v.bandwidth)) : undefined;
  }, [stream]);

  if (isLoading && !stream) return <Skeleton variant="rect" height={300} />;

  if (isError || !stream) {
    return (
      <Alert
        tone="danger"
        title="Couldn't load stream"
        actions={
          <Button size="sm" variant="secondary" startIcon={<ArrowLeft size={14} />} onClick={() => navigate(`${base}/streams`)}>
            Back to streams
          </Button>
        }
      >
        {(error as { message?: string })?.message ?? `Stream "${id}" not found.`}
      </Alert>
    );
  }

  const isLive = stream.kind === 'live';

  return (
    <>
      <PageHeader
        icon={<Radio size={20} />}
        breadcrumbs={
          <Breadcrumbs>
            <BreadcrumbItem asChild><Link to={base}>DRM Management</Link></BreadcrumbItem>
            <BreadcrumbItem asChild><Link to={`${base}/streams`}>Sources & Streams</Link></BreadcrumbItem>
            <BreadcrumbItem current>{stream.id}</BreadcrumbItem>
          </Breadcrumbs>
        }
        title={stream.id}
        actions={
          <div className={styles.headerTags}>
            <Badge tone={kindTone(stream.kind)} appearance="subtle" size="sm">{KIND_LABEL[stream.kind]}</Badge>
            <StatusPill tone={streamStatusTone(stream.status)}>{STREAM_STATUS_LABEL[stream.status]}</StatusPill>
            {stream.status === 'live' && (
              <Button
                variant="secondary"
                startIcon={<Square size={15} />}
                disabled={stop.isPending}
                onClick={() => setPendingStop(true)}
              >
                Stop
              </Button>
            )}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <IconButton variant="ghost" aria-label="More stream actions">
                  <MoreHorizontal size={18} />
                </IconButton>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onSelect={() => setPendingConfig(true)}>
                  <SlidersHorizontal size={14} /> Configure key policy
                </DropdownMenuItem>
                {stream.output_url && (
                  <DropdownMenuItem onSelect={copyUrl}>
                    <Copy size={14} /> Copy stream URL
                  </DropdownMenuItem>
                )}
                <DropdownMenuItem tone="danger" onSelect={() => setPendingDelete(true)}>
                  <Trash2 size={14} /> Delete stream
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        }
      />

      {/* Stream details — identity + addressing facts, up top under the header. */}
      <StreamSummaryCard stream={stream} />

      {/* Metrics — CompactStat inside a flat Card, the console's detail-page KPI pattern. */}
      <Card flat className={styles.blockGap}>
        <CardHeader>
          <CardTitle className={styles.cardTitle}>Metrics</CardTitle>
        </CardHeader>
        <CardBody>
          <CompactStatGroup columns={4}>
            <CompactStat label="Renditions" value={stream.abr_ladder?.length ?? 0} />
            <CompactStat label="Peak bitrate" value={fmtBitrate(peakBitrate)} />
            <CompactStat label="DVR window" value={fmtDuration(stream.dvr_seconds)} />
            <CompactStat label="Key period" value={isLive ? `${stream.period_secs}s` : '—'} hint={isLive ? 'live rotation' : undefined} />
          </CompactStatGroup>
        </CardBody>
      </Card>

      {/* Live preview — the encrypted HLS the player consumes. hls.js fetches the
          AES-128 key per rotation period (subject to the key server's auth/CORS). */}
      {stream.output_url && (
        <div className={styles.blockGap}>
          <HlsPreviewPlayer
            src={stream.output_url}
            live={isLive}
            title={stream.id}
            description="Encrypted HLS (AES-128). Keys are fetched per rotation period."
            statsDescription="Live hls.js metrics for the encrypted stream."
          />
        </div>
      )}

      {/* Key rotation — live ops' primary view, given the full width. */}
      {isLive && (
        <Card flat className={styles.blockGap}>
          <CardHeader>
            <CardTitle className={styles.cardTitle}>Key rotation</CardTitle>
          </CardHeader>
          <CardBody>
            <RotationLiveView streamId={stream.id} periodSecs={stream.period_secs} dvrSeconds={stream.dvr_seconds} />
          </CardBody>
        </Card>
      )}

      {/* Delivery pipeline — the template's full-width FlowDiagram hero. */}
      <Card flat className={styles.blockGap}>
        <CardHeader>
          <CardTitle className={styles.cardTitle}>Delivery pipeline</CardTitle>
        </CardHeader>
        <CardBody>
          <StreamTopology stream={stream} />
        </CardBody>
      </Card>

      {/* ABR ladder — full-width renditions table. */}
      <Card flat className={styles.blockGap}>
        <CardHeader>
          <CardTitle className={styles.cardTitle}>ABR ladder</CardTitle>
        </CardHeader>
        <CardBody>
          <DataTable
            columns={renditionColumns}
            data={stream.abr_ladder ?? []}
            getRowId={(v) => v.uri}
            dense
            emptyState={<EmptyState icon={<Layers size={20} />} title="No ABR renditions yet" description="Rendition details are populated once the packager processes the source." />}
          />
        </CardBody>
      </Card>

      <StreamConfigDialog open={pendingConfig} onOpenChange={setPendingConfig} stream={stream} />

      <ConfirmDialog
        open={pendingStop}
        onOpenChange={setPendingStop}
        title={`Stop "${stream.id}"?`}
        description="The packager stops encrypting and the live edge ends. Viewers lose playback. DVR content remains for the configured window."
        confirmLabel="Stop stream"
        destructive
        loading={stop.isPending}
        onConfirm={() =>
          stop.mutate(stream.id, {
            onSuccess: () => {
              show({ tone: 'success', title: `Stream "${stream.id}" stopped` });
              trackFeatureAction('aes', 'stream', 'disable');
              setPendingStop(false);
            },
            onError: (e) => { show({ tone: 'danger', title: (e as Error)?.message || "Couldn't stop stream" }); setPendingStop(false); },
          })
        }
      />

      <ConfirmDialog
        open={pendingDelete}
        onOpenChange={setPendingDelete}
        title={`Delete "${stream.id}"?`}
        description="The stream registration, shard assignment and key policy are removed. This cannot be undone."
        confirmLabel="Delete"
        destructive
        loading={del.isPending}
        onConfirm={() =>
          del.mutate(stream.id, {
            onSuccess: () => {
              show({ tone: 'success', title: `Stream "${stream.id}" deleted` });
              trackFeatureAction('aes', 'stream', 'delete');
              navigate(`${base}/streams`);
            },
            onError: (e) => { show({ tone: 'danger', title: (e as Error)?.message || "Couldn't delete stream" }); setPendingDelete(false); },
          })
        }
      />
    </>
  );
}

export default StreamDetail;
